// src/pages/CodebookPage.tsx
// Bookmate feature: Interactive programming tutorials with executable code examples

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import ThemeToggle from '../components/ThemeToggle';
import codebookApi from '../services/codebookApi';
import { CodebookGenerateRequest, CodebookGetResponse, CodebookListItem } from '../types/chat';
import CodebookViewer from '../components/Codebook/CodebookViewer';
import CodebookHistory from '../components/Codebook/CodebookHistory';
import DocumentationButton from '../components/Documentation/DocumentationButton';
import DocumentationModal from '../components/Documentation/DocumentationModal';
import { useCodebookStore } from '../stores/codebookStore';
import { getRandomTagline } from '../utils/taglines';
import { trackBookmateGenerated } from '../utils/analytics';

const CodebookPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Zustand store — persists across tab switches
  const {
    input, setInput,
    isGenerating, setIsGenerating,
    statusMessage, setStatusMessage,
    error, setError,
    currentCodebook, setCurrentCodebook,
    showViewer, setShowViewer,
    elapsedSeconds, setElapsedSeconds,
    activeCodebookId, setActiveCodebookId,
    pathContext, setPathContext,
  } = useCodebookStore();

  const [activeTab, setActiveTab] = useState<'generate' | 'tutorials'>('generate');
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState('');
  const [isPromptFromClick, setIsPromptFromClick] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [documentationOpen, setDocumentationOpen] = useState(false);

  // Pre-fill prompt from URL query param (e.g. /bookmate?prompt=...).
  // If &autosubmit=1 is also present (concierge "yes" flow), kick off generation
  // immediately so the student doesn't have to click Generate after already saying yes.
  //
  // Bare /bookmate (sidebar click, no ?prompt) -> always land on the home view,
  // never silently restore a previously-viewed tutorial from Zustand state. The
  // student can still continue an in-flight tutorial via the "Continue" card on
  // the home view, but the default is a fresh state.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const promptParam = params.get('prompt');
    const autosubmit = params.get('autosubmit') === '1';
    // Learning Path "Start →" links carry &path=&node= — remember them so the
    // viewer can swap "What's Next?" for a "Back to your path" card.
    const pathParam = params.get('path');
    const nodeParam = params.get('node');
    if (promptParam) {
      setInput(promptParam);
      setPathContext(
        pathParam && nodeParam ? { pathId: pathParam, nodeId: nodeParam } : null
      );
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      if (autosubmit) {
        console.log('[Bookmate] autosubmit=1 detected, starting generation:', promptParam);
        runGeneration(promptParam);
      }
    } else if (!isGenerating) {
      // Bare /bookmate with no in-flight generation: show the home view, not the
      // last completed tutorial. currentCodebook stays in state so the
      // "Continue last tutorial" card can offer it.
      setShowViewer(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Elapsed timer during generation (don't reset on remount — Zustand preserves the value)
  useEffect(() => {
    if (isGenerating) {
      timerRef.current = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isGenerating]);

  const featureCards = [
    {
      icon: '📖',
      title: 'Learn with Clarity',
      description: 'Clear explanations and examples',
    },
    {
      icon: '</>',
      title: 'Executable Code',
      description: 'Run and test code blocks real time',
    },
    {
      icon: '📥',
      title: 'Download PDF',
      description: 'Keep tutorials available offline',
    },
    {
      icon: '✨',
      title: 'Learn Faster',
      description: 'AI-guided learning paths',
    },
  ];

  const examplePrompts = [
    'C programming with pointers and memory allocation from scratch',
    'I know C++ pointers and memory, teach me how Rust does it differently',
    'Give me top 10 python interview questions for intermediate level with programming examples',
    'How does error handling work in Go vs C++?',
  ];

  const handlePromptClick = (prompt: string) => {
    setInput(prompt);
    setIsPromptFromClick(true);
  };

  const KNOWN_LANGUAGES = [
    'python', 'javascript', 'typescript', 'java', 'c++', 'cpp', 'c#', 'csharp',
    'go', 'golang', 'rust', 'ruby', 'php', 'swift', 'kotlin', 'scala',
    'r', 'dart', 'lua', 'perl', 'haskell', 'elixir', 'clojure',
  ];

  const detectLanguageCount = (prompt: string): number => {
    const lower = prompt.toLowerCase();
    const found = KNOWN_LANGUAGES.filter(lang => {
      const pattern = new RegExp(`\\b${lang.replace('+', '\\+')}\\b`);
      return pattern.test(lower);
    });
    // Deduplicate aliases (cpp/c++, golang/go, csharp/c#)
    const unique = new Set(found.map(l =>
      l === 'cpp' ? 'c++' : l === 'golang' ? 'go' : l === 'csharp' ? 'c#' : l
    ));
    return unique.size;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isGenerating) return;

    const langCount = detectLanguageCount(input.trim());
    if (langCount > 2) {
      setError('Bookmate supports up to 2 languages per tutorial. Please rewrite your prompt with only 2 languages. Support for 3+ languages is coming soon!');
      return;
    }

    setPendingPrompt(input.trim());
    setShowConfirm(true);
  };

  // Poll a codebook until completed or failed
  const pollCodebook = async (codebookId: string) => {
    let codebook = null;
    let attempts = 0;
    const maxAttempts = 240;

    while (attempts < maxAttempts) {
      // Check if still generating (user might have clicked back)
      if (!useCodebookStore.getState().isGenerating) break;

      await new Promise(resolve => setTimeout(resolve, 1000));

      try {
        const fetched = await codebookApi.getCodebook(codebookId);
        console.log(`[Bookmate] Poll ${attempts + 1}/${maxAttempts}: status=${fetched.status} blocks=${fetched.blocks?.length ?? 0}`);

        if (fetched.status === 'generating') {
          const blockCount = fetched.total_blocks || 0;
          const narrativeCount = fetched.narrative_blocks || 0;
          const codeCount = fetched.code_blocks || 0;

          if (blockCount === 0) {
            setStatusMessage('Analyzing your request and building structure...');
          } else if (narrativeCount > 0 && codeCount === 0) {
            setStatusMessage(`Writing explanations • ${narrativeCount} section${narrativeCount !== 1 ? 's' : ''} done`);
          } else if (codeCount > 0) {
            setStatusMessage(`Building examples • ${codeCount} code block${codeCount !== 1 ? 's' : ''}, ${narrativeCount} explanation${narrativeCount !== 1 ? 's' : ''}`);
          } else {
            setStatusMessage(`Generating tutorial • ${blockCount} section${blockCount !== 1 ? 's' : ''} in progress`);
          }
        } else if (fetched.status === 'completed') {
          setStatusMessage(`Tutorial ready • ${fetched.total_blocks} section${fetched.total_blocks !== 1 ? 's' : ''} created`);
        } else if (fetched.status === 'failed') {
          setStatusMessage(`Failed: ${fetched.error_message || 'An error occurred'}`);
        }

        // Live-stream: as soon as the first block lands, swap into the viewer
        // and keep updating the viewer on every poll so the user watches it fill in.
        if (fetched.blocks && fetched.blocks.length > 0) {
          setCurrentCodebook(fetched);
          if (!useCodebookStore.getState().showViewer) {
            setShowViewer(true);
          }
        }

        if (fetched.status === 'completed') {
          console.log('[Bookmate] Generation completed!');
          codebook = fetched;
          break;
        } else if (fetched.status === 'failed') {
          throw new Error(`Generation failed: ${fetched.error_message || 'Unknown error'}`);
        }

        attempts++;
      } catch (err) {
        console.error(`[Bookmate] Poll ${attempts + 1} error:`, err);
        attempts++;
      }
    }

    if (codebook) {
      setCurrentCodebook(codebook);
      setShowViewer(true);
      setActiveCodebookId(null);
      setInput('');
    } else if (useCodebookStore.getState().isGenerating) {
      setError('Timeout waiting for tutorial generation');
    }
    setIsGenerating(false);
  };

  // Resume polling on remount if generation was in progress
  useEffect(() => {
    if (isGenerating && activeCodebookId) {
      console.log('[Bookmate] Resuming poll for:', activeCodebookId);
      pollCodebook(activeCodebookId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generation body, extracted so both the confirm-modal flow and the concierge
  // auto-submit flow (?prompt=...&autosubmit=1) can trigger it directly.
  const runGeneration = async (prompt: string) => {
    setShowConfirm(false);
    setPendingPrompt('');

    setIsGenerating(true);
    setElapsedSeconds(0);
    setError(null);
    setStatusMessage('Starting generation...');

    try {
      const sessionId = `session_${Date.now()}`;
      // Path attribution (paths step 3): tag the generation so the backend's
      // learning_event marks the path node completed.
      const pathCtx = useCodebookStore.getState().pathContext;
      const request: CodebookGenerateRequest = {
        prompt: prompt,
        session_id: sessionId,
        ...(pathCtx ? { path_id: pathCtx.pathId, node_id: pathCtx.nodeId } : {}),
      };

      const response = await codebookApi.generateCodebook(request);
      console.log('[Bookmate] Generation started:', response.codebook_id);
      trackBookmateGenerated(prompt);
      setActiveCodebookId(response.codebook_id);

      await pollCodebook(response.codebook_id);
    } catch (err: any) {
      console.error('[Bookmate] Error:', err);
      const detail = err.response?.data?.detail || err.message || 'Failed to generate tutorial';
      const is402 = err.response?.status === 402 || detail.includes('Upgrade') || detail.includes('Insufficient');
      // v3.19.7.1: surface the backend's actual 402 detail (e.g. "You've used all 2 Starter
      // Bookmates. Upgrade to Pro for unlimited Bookmates.") instead of the old hardcoded
      // v3.18 "Basic plan includes 5 chat messages..." copy.
      setError(detail);
      setIsGenerating(false);
      if (is402) {
        // Route to /pay with a reason so PayPage shows a tailored headline. The bookmate_cap
        // reason matches the wording used in PayPage's ?reason switch.
        const isBookmateCap = detail.toLowerCase().includes('starter bookmates') || detail.toLowerCase().includes('bookmate limit');
        const reason = isBookmateCap ? 'bookmate_cap' : 'trial_expired';
        setTimeout(() => navigate(`/pay?reason=${reason}`), 2500);
      }
    }
  };

  const handleConfirmGenerate = () => runGeneration(pendingPrompt);

  const handleBack = () => {
    useCodebookStore.getState().resetGeneration();
  };

  const handleLoadFromHistory = async (codebook: CodebookListItem) => {
    try {
      setError(null);
      setIsGenerating(true);
      setIsPromptFromClick(false);
      // An old tutorial from My Tutorials is not a path step — normal ending.
      setPathContext(null);
      setStatusMessage('Loading tutorial...');
      const fullCodebook = await codebookApi.getCodebook(codebook.codebook_id);
      setCurrentCodebook(fullCodebook);
      setShowViewer(true);
      setActiveTab('generate');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load tutorial');
    } finally {
      setIsGenerating(false);
      setStatusMessage('');
    }
  };


  return (
    <div className="flex flex-col h-screen bg-background text-foreground overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="flex items-center justify-between px-3 py-3 md:px-6 md:py-4">
          <div className="flex items-center space-x-2 md:space-x-4 pl-10 md:pl-0">
            <img
              src="/cubemate-logo.png"
              alt="Cubemate"
              className="w-8 h-8 md:w-10 md:h-10 rounded-lg"
            />
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-base md:text-xl font-bold bg-gradient-to-r from-primary to-brand bg-clip-text text-transparent">
                  Cubemate
                </h1>
              </div>
              <p className="hidden md:block text-xs text-muted-foreground/80 font-normal italic">
                {getRandomTagline()}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-1.5 md:space-x-3">
            {(user as any)?.subscription_tier !== 'ultimate' && (
              <button
                onClick={() => navigate('/settings?tab=subscription')}
                className="hidden md:block px-4 py-2 text-sm bg-gradient-to-r from-[#8E3BCE] to-[#6D2D8D] text-white hover:shadow-lg hover:shadow-primary/30 rounded-lg transition-all duration-200 font-semibold"
                title="Upgrade to premium"
              >
                Upgrade +
              </button>
            )}
            <span className="hidden md:inline-flex"><DocumentationButton onClick={() => setDocumentationOpen(true)} /></span>
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Body: Sidebar + Secondary Panel + Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <Sidebar />

        {/* Secondary Panel for Tabs (below header) - hide when viewing tutorial, hidden on mobile */}
        {!showViewer && (
          <div className="hidden md:block w-48 bg-muted/30 border-r border-border flex-shrink-0">
            <div className="p-3 space-y-1">
              {/* Generate Tab */}
              <button
                onClick={() => setActiveTab('generate')}
                className={`w-full flex items-center space-x-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'generate'
                    ? 'bg-white dark:bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <span className="text-base">✨</span>
                <span>Generate</span>
              </button>

              {/* My Tutorials Tab */}
              <button
                onClick={() => setActiveTab('tutorials')}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'tutorials'
                    ? 'bg-white dark:bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-base">📚</span>
                  <span>My tutorial</span>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile Tab Bar — shown only on mobile when not viewing tutorial */}
          {!showViewer && (
            <div className="md:hidden flex border-b border-border bg-muted/30">
              <button
                onClick={() => setActiveTab('generate')}
                className={`flex-1 py-2.5 text-sm font-medium text-center transition-all ${
                  activeTab === 'generate'
                    ? 'text-foreground border-b-2 border-primary'
                    : 'text-muted-foreground'
                }`}
              >
                Generate
              </button>
              <button
                onClick={() => setActiveTab('tutorials')}
                className={`flex-1 py-2.5 text-sm font-medium text-center transition-all ${
                  activeTab === 'tutorials'
                    ? 'text-foreground border-b-2 border-primary'
                    : 'text-muted-foreground'
                }`}
              >
                My Tutorials
              </button>
            </div>
          )}

          {/* Error Banner */}
          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 mx-4 mt-4 rounded-lg flex justify-between items-center">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-red-500 hover:text-red-600">✕</button>
            </div>
          )}

          {/* Confirmation Dialog */}
          {showConfirm && (
            <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white dark:bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
                <h3 className="text-lg font-semibold text-foreground mb-2">Generate Tutorial?</h3>
                <p className="text-sm text-muted-foreground mb-4">This will use credits to generate a tutorial for:</p>
                <div className="bg-muted/50 border border-border rounded-lg p-3 mb-4">
                  <p className="text-sm text-foreground italic">"{pendingPrompt}"</p>
                </div>
                <p className="text-xs text-muted-foreground mb-6">This usually takes 1-2 minutes to prepare. Please don't leave the page.</p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => { setShowConfirm(false); setPendingPrompt(''); }}
                    className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmGenerate}
                    className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#8E3BCE] to-[#6D2D8D] hover:shadow-lg rounded-lg transition-all"
                  >
                    Generate
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tutorial Viewer */}
          {showViewer && currentCodebook ? (
            <CodebookViewer
              codebook={currentCodebook}
              onBack={handleBack}
              showPrompt={!isPromptFromClick}
              pathContext={pathContext}
              onGenerateNext={(prompt) => {
                setShowViewer(false);
                setCurrentCodebook(null);
                setInput(prompt);
                setPendingPrompt(prompt);
                setIsPromptFromClick(true);
                setShowConfirm(true);
              }}
            />
          ) : (
            /* Main Content Area */
            <div className="flex-1 overflow-y-auto">
              <div className="max-w-5xl mx-auto px-4 py-4 md:px-8 md:py-6 flex flex-col items-center">
                {activeTab === 'generate' ? (
                  <>
                    {/* Feature Cards */}
                    <div className="w-full max-w-[900px] mb-8 bg-white dark:bg-card border border-border rounded-2xl p-6 shadow-sm">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        {featureCards.map((card, index) => (
                          <div
                            key={index}
                            className="flex flex-col items-center text-center space-y-1"
                          >
                            <div className="text-3xl mb-1">{card.icon}</div>
                            <h3 className="text-sm font-semibold text-foreground">
                              {card.title}
                            </h3>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {card.description}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Quote */}
                    <p className="text-2xl md:text-3xl text-foreground mb-8 text-center font-light">
                      Turn lessons into live code—instantly.
                    </p>
                    <p className="text-xl md:text-2xl text-muted-foreground italic mb-8 text-center">
                      What are you learning today?
                    </p>

                    {/* Input Form */}
                    <form onSubmit={handleSubmit} className="w-full max-w-2xl mb-8">
                      <div className="relative flex items-center bg-white dark:bg-card border border-border/30 rounded-full p-2 shadow-lg hover:shadow-xl transition-all">
                        <button
                          type="button"
                          className="flex-shrink-0 w-9 h-9 text-muted-foreground hover:text-foreground rounded-full focus:outline-none transition-all flex items-center justify-center hover:bg-muted/50"
                          title="Add attachments"
                        >
                          <span className="text-xl font-light">+</span>
                        </button>
                        <input
                          type="text"
                          value={input}
                          onChange={(e) => { setInput(e.target.value); setIsPromptFromClick(false); }}
                          placeholder="Try: 'Error handling in Python vs Java' or 'Pointers in C++ vs Rust'"
                          className="flex-1 px-4 py-2 bg-transparent text-foreground placeholder-muted-foreground/60 focus:outline-none text-sm"
                          disabled={isGenerating}
                        />
                        <button
                          type="submit"
                          disabled={!input.trim() || isGenerating}
                          className="flex-shrink-0 w-9 h-9 bg-gradient-to-r from-[#8E3BCE] to-[#6D2D8D] text-white rounded-full hover:shadow-lg hover:scale-105 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
                        >
                          {isGenerating ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          ) : (
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="22" y1="2" x2="11" y2="13"></line>
                              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                            </svg>
                          )}
                        </button>
                      </div>

                      {/* Generation Status */}
                      {isGenerating && statusMessage && (
                        <div className="mt-3 text-center text-muted-foreground space-y-1">
                          <div className="text-sm flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-3 w-3 border-2 border-primary border-t-transparent"></div>
                            <span>{statusMessage}</span>
                          </div>
                          <p className="text-xs">
                            {Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')} elapsed · usually takes 1-2 minutes
                          </p>
                        </div>
                      )}
                    </form>

                    {/* Continue last tutorial — only when there's a finished tutorial
                        in state and we're idle. Lets students resume without losing
                        their place when they navigate back to /bookmate. */}
                    {!isGenerating && currentCodebook && currentCodebook.status === 'completed' && (
                      <button
                        onClick={() => setShowViewer(true)}
                        className="w-full max-w-2xl mb-6 text-left rounded-xl border border-primary/30 bg-primary/5 hover:bg-primary/10 hover:border-primary/50 p-4 transition-all group"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-xl flex-shrink-0">📖</span>
                            <div className="min-w-0">
                              <p className="text-xs text-muted-foreground mb-0.5">Continue last tutorial</p>
                              <p className="text-sm font-medium text-foreground truncate">
                                {currentCodebook.title || 'Untitled tutorial'}
                              </p>
                            </div>
                          </div>
                          <span className="text-primary text-lg flex-shrink-0 group-hover:translate-x-0.5 transition-transform">→</span>
                        </div>
                      </button>
                    )}

                    {/* Example Prompts */}
                    {!isGenerating && (
                      <div className="w-full max-w-2xl">
                        <h3 className="text-xs font-medium text-center text-muted-foreground mb-4">
                          Examples Prompts
                        </h3>
                        <div>
                          {examplePrompts.map((prompt, index) => (
                            <button
                              key={index}
                              onClick={() => handlePromptClick(prompt)}
                              className="w-full text-center px-4 py-3 bg-transparent hover:text-primary text-foreground text-sm transition-all border-b border-border/50 last:border-b-0"
                            >
                              {prompt}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  /* My Tutorials View - uses CodebookHistory component */
                  <div className="w-full">
                    <CodebookHistory onSelectCodebook={handleLoadFromHistory} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Documentation Modal */}
      <DocumentationModal
        isOpen={documentationOpen}
        onClose={() => setDocumentationOpen(false)}
        onPromptClick={(prompt: string) => {
          setDocumentationOpen(false);
          setInput(prompt);
          setPendingPrompt(prompt);
          setIsPromptFromClick(true);
          setShowConfirm(true);
        }}
        currentRoute="bookmate"
      />
    </div>
  );
};

export default CodebookPage;
